/**
 * Support & Suggestions Service
 * API client for support tickets and feature suggestions
 */
import { apiClient } from '../apiClient'
import { API_CONFIG } from '@/config/api'
import { authService } from '../../auth/authService'

// ==================== Types ====================

export interface SupportTicket {
  id: number
  ticket_id: string
  submitter_name: string
  submitter_email?: string
  category: string
  subject: string
  description: string
  priority: string
  status: string
  current_page?: string
  screenshot_url?: string
  screenshot_filename?: string
  email_updates: boolean
  assigned_to_user_id?: number
  assigned_to_name?: string
  admin_notes?: string
  resolution_notes?: string
  has_new_response: boolean
  created_at: string
  updated_at: string
  resolved_at?: string
}

export interface SupportAssignee {
  user_id: number
  display_name: string
  email: string
}

export interface Suggestion {
  id: number
  suggestion_id: string
  submitter_name: string
  submitter_email?: string
  category: string
  title: string
  description: string
  impact_area?: string
  business_value?: string
  status: string
  admin_response?: string
  vote_count: number
  has_new_response: boolean
  created_at: string
  updated_at: string
  implemented_at?: string
}

export interface SupportStats {
  total_tickets: number
  pending_tickets: number
  in_progress_tickets: number
  resolved_tickets: number
  total_suggestions: number
  pending_suggestions: number
  implemented_suggestions: number
  avg_resolution_hours?: number
  total_training_materials: number
  published_training_materials: number
}

export interface TrainingMaterial {
  id: number
  material_id: string
  title: string
  description?: string
  content_html?: string
  category: string
  content_type: string
  external_url?: string
  video_url?: string
  file_url?: string
  file_filename?: string
  file_size_bytes?: number
  video_blob_path?: string
  video_blob_filename?: string
  video_blob_size_bytes?: number
  caption_blob_path?: string
  caption_filename?: string
  surface_key?: string | null
  is_published: boolean
  is_pinned: boolean
  sort_order: number
  created_by_name: string
  view_count: number
  created_at: string
  updated_at: string
}

export interface CreateTrainingMaterialData {
  title: string
  category: string
  content_type: string
  description?: string
  content_html?: string
  external_url?: string
  video_url?: string
  surface_key?: string
  is_published?: boolean
  is_pinned?: boolean
  sort_order?: number
  attachment?: File
  video_file?: File
  caption_file?: File
}

export interface UpdateTrainingMaterialData {
  title?: string
  description?: string
  content_html?: string
  category?: string
  content_type?: string
  external_url?: string
  video_url?: string
  surface_key?: string
  is_published?: boolean
  is_pinned?: boolean
  sort_order?: number
  attachment?: File
  video_file?: File
  caption_file?: File
  remove_video?: boolean
  remove_file?: boolean
  remove_caption?: boolean
}

export interface CreateTicketData {
  category: string
  subject: string
  description: string
  priority: string
  current_page?: string
  browser_info?: string
  email_updates: boolean
  screenshot?: File
}

export interface CreateDataCorrectionData {
  affected_fields: string[]
  description: string
  issue_type?: string
  current_page?: string
  browser_info?: string
}

export interface DataCorrectionTicket {
  ticket_id: string
  status: string
  source: string
  issue_type: string
  affected_fields: string[]
  user_description: string
  description: string
  assigned_to_name?: string | null
  resolution_notes?: string | null
  created_at: string
  updated_at?: string
  resolved_at?: string | null
}

export interface CreateSuggestionData {
  category: string
  title: string
  description: string
  impact_area?: string
  business_value?: string
}

export interface UpdateTicketData {
  status?: string
  admin_notes?: string
  resolution_notes?: string
  assigned_to_name?: string
  assigned_to_user_id?: number
}

export interface UpdateSuggestionData {
  status?: string
  admin_response?: string
}

// ==================== Constants ====================

export const TICKET_CATEGORIES = [
  { value: 'bug_error', label: 'Bug / Error' },
  { value: 'access_issue', label: 'Access Issue' },
  { value: 'data_problem', label: 'Data Problem' },
  { value: 'how_to_question', label: 'How-To Question' },
  { value: 'performance_issue', label: 'Platform Performance Issue' },
  { value: 'other', label: 'Other' },
]

export const TICKET_PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
]

export const TICKET_STATUSES = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_review', label: 'In Review' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
]

export const SUGGESTION_CATEGORIES = [
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'ui_ux_improvement', label: 'UI/UX Improvement' },
  { value: 'process_improvement', label: 'Process Improvement' },
  { value: 'integration_request', label: 'Integration Request' },
  { value: 'other', label: 'Other' },
]

export const SUGGESTION_STATUSES = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_review', label: 'In Review' },
  { value: 'planned', label: 'Planned' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'implemented', label: 'Implemented' },
  { value: 'declined', label: 'Declined' },
]

export const IMPACT_AREAS = [
  'Team Member Journey',
  'People Leader Hub',
  'Reviews & Check-ins',
  'Objectives & KPAs',
  'Development & IDP',
  'Feedback System',
  'Calibration & Talent Review',
  'Analytics & Reporting',
  'Admin & Configuration',
  'Integration (F&O)',
  'General Platform',
]

export const TRAINING_CATEGORIES = [
  { value: 'getting_started', label: 'Getting Started' },
  { value: 'objectives_kpas', label: 'Objectives & KPAs' },
  { value: 'reviews_checkins', label: 'Reviews & Check-ins' },
  { value: 'feedback', label: 'Feedback' },
  { value: 'development_idp', label: 'Development & IDP' },
  { value: 'manager_guide', label: 'Manager Guide' },
  { value: 'hr_admin_guide', label: 'HR Admin Guide' },
  { value: 'calibration_talent', label: 'Calibration & Talent' },
  { value: 'integrations', label: 'Integrations' },
  { value: 'policies_compliance', label: 'Policies & Compliance' },
  { value: 'faq', label: 'FAQ' },
  { value: 'other', label: 'Other' },
]

export const TRAINING_CONTENT_TYPES = [
  { value: 'article', label: 'Article' },
  { value: 'link', label: 'External Link' },
  { value: 'file', label: 'File Download' },
  { value: 'video', label: 'Video' },
  { value: 'mixed', label: 'Mixed Content' },
]

// ==================== Service ====================

class SupportService {
  private baseUrl = '/api/v1/identity/support'

  // ---------- Tickets ----------

  async createDataCorrectionTicket(
    data: CreateDataCorrectionData
  ): Promise<{ ticket_id: string; status: string; message: string }> {
    const response = await apiClient.post<{ ticket_id: string; status: string; message: string }>(
      `${this.baseUrl}/data-correction-tickets`,
      data
    )
    return response.data
  }

  async createTicket(data: CreateTicketData): Promise<SupportTicket> {
    const formData = new FormData()
    formData.append('category', data.category)
    formData.append('subject', data.subject)
    formData.append('description', data.description)
    formData.append('priority', data.priority)
    formData.append('email_updates', String(data.email_updates))

    if (data.current_page) formData.append('current_page', data.current_page)
    if (data.browser_info) formData.append('browser_info', data.browser_info)
    if (data.screenshot) formData.append('screenshot', data.screenshot)

    const response = await apiClient.post<SupportTicket>(`${this.baseUrl}/tickets`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  }

  // Screenshot bytes require the auth header, so a plain <img src> can't load them
  // (and in PROD the frontend origin doesn't proxy /api at all) — fetch as a blob.
  async getTicketScreenshot(ticketId: string): Promise<Blob> {
    const response = await apiClient.get<Blob>(`${this.baseUrl}/tickets/${ticketId}/screenshot`, {
      responseType: 'blob',
    })
    return response.data
  }

  async getMyTickets(): Promise<{ tickets: SupportTicket[]; total: number }> {
    const response = await apiClient.get<{ tickets: SupportTicket[]; total: number }>(`${this.baseUrl}/tickets/mine`)
    return response.data
  }

  async getMyDataCorrections(): Promise<{ tickets: DataCorrectionTicket[]; total: number }> {
    const response = await apiClient.get<{ tickets: DataCorrectionTicket[]; total: number }>(
      `${this.baseUrl}/data-correction-tickets/mine`
    )
    return response.data
  }

  async getAllTickets(params?: {
    status?: string
    category?: string
    priority?: string
    search?: string
    limit?: number
    offset?: number
  }): Promise<{ tickets: SupportTicket[]; total: number }> {
    const response = await apiClient.get<{ tickets: SupportTicket[]; total: number }>(`${this.baseUrl}/tickets`, { params })
    return response.data
  }

  async getTicket(ticketId: string): Promise<SupportTicket> {
    const response = await apiClient.get<SupportTicket>(`${this.baseUrl}/tickets/${ticketId}`)
    return response.data
  }

  async updateTicket(ticketId: string, data: UpdateTicketData): Promise<SupportTicket> {
    const response = await apiClient.patch<SupportTicket>(`${this.baseUrl}/tickets/${ticketId}`, data)
    return response.data
  }

  // ---------- Assignees ----------

  async getAssignees(): Promise<SupportAssignee[]> {
    const response = await apiClient.get<SupportAssignee[]>(`${this.baseUrl}/assignees`)
    return response.data
  }

  // ---------- Suggestions ----------

  async createSuggestion(data: CreateSuggestionData): Promise<Suggestion> {
    const response = await apiClient.post<Suggestion>(`${this.baseUrl}/suggestions`, data)
    return response.data
  }

  async getMySuggestions(): Promise<{ suggestions: Suggestion[]; total: number }> {
    const response = await apiClient.get<{ suggestions: Suggestion[]; total: number }>(`${this.baseUrl}/suggestions/mine`)
    return response.data
  }

  async getAllSuggestions(params?: {
    status?: string
    category?: string
    search?: string
    limit?: number
    offset?: number
  }): Promise<{ suggestions: Suggestion[]; total: number }> {
    const response = await apiClient.get<{ suggestions: Suggestion[]; total: number }>(`${this.baseUrl}/suggestions`, { params })
    return response.data
  }

  async getSuggestion(suggestionId: string): Promise<Suggestion> {
    const response = await apiClient.get<Suggestion>(`${this.baseUrl}/suggestions/${suggestionId}`)
    return response.data
  }

  async updateSuggestion(suggestionId: string, data: UpdateSuggestionData): Promise<Suggestion> {
    const response = await apiClient.patch<Suggestion>(`${this.baseUrl}/suggestions/${suggestionId}`, data)
    return response.data
  }

  // ---------- Training Materials ----------

  async getPublishedTraining(params?: {
    category?: string
    search?: string
    limit?: number
    offset?: number
  }): Promise<{ materials: TrainingMaterial[]; total: number }> {
    const response = await apiClient.get<{ materials: TrainingMaterial[]; total: number }>(`${this.baseUrl}/training`, { params })
    return response.data
  }

  async getAllTraining(params?: {
    category?: string
    published?: boolean
    search?: string
    limit?: number
    offset?: number
  }): Promise<{ materials: TrainingMaterial[]; total: number }> {
    const response = await apiClient.get<{ materials: TrainingMaterial[]; total: number }>(`${this.baseUrl}/training/admin/all`, { params })
    return response.data
  }

  async getTrainingMaterial(materialId: string): Promise<TrainingMaterial> {
    const response = await apiClient.get<TrainingMaterial>(`${this.baseUrl}/training/${materialId}`)
    return response.data
  }

  /** The published guidance video bound to a page/dialog surface, or null. Powers VideoHelpButton. */
  async getTrainingMaterialBySurface(surfaceKey: string): Promise<TrainingMaterial | null> {
    const response = await apiClient.get<{ material: TrainingMaterial | null }>(
      `${this.baseUrl}/training/by-surface/${surfaceKey}`
    )
    return response.data?.material ?? null
  }

  async createTrainingMaterial(data: CreateTrainingMaterialData): Promise<TrainingMaterial> {
    const formData = new FormData()
    formData.append('title', data.title)
    formData.append('category', data.category)
    formData.append('content_type', data.content_type)
    if (data.description) formData.append('description', data.description)
    if (data.content_html) formData.append('content_html', data.content_html)
    if (data.external_url) formData.append('external_url', data.external_url)
    if (data.video_url) formData.append('video_url', data.video_url)
    if (data.surface_key !== undefined) formData.append('surface_key', data.surface_key)
    if (data.is_published !== undefined) formData.append('is_published', String(data.is_published))
    if (data.is_pinned !== undefined) formData.append('is_pinned', String(data.is_pinned))
    if (data.sort_order !== undefined) formData.append('sort_order', String(data.sort_order))
    if (data.attachment) formData.append('attachment', data.attachment)
    if (data.video_file) formData.append('video_file', data.video_file)
    if (data.caption_file) formData.append('caption_file', data.caption_file)

    const response = await apiClient.post<TrainingMaterial>(`${this.baseUrl}/training`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  }

  async updateTrainingMaterial(materialId: string, data: UpdateTrainingMaterialData): Promise<TrainingMaterial> {
    const formData = new FormData()
    if (data.title !== undefined) formData.append('title', data.title)
    if (data.description !== undefined) formData.append('description', data.description)
    if (data.content_html !== undefined) formData.append('content_html', data.content_html)
    if (data.category !== undefined) formData.append('category', data.category)
    if (data.content_type !== undefined) formData.append('content_type', data.content_type)
    if (data.external_url !== undefined) formData.append('external_url', data.external_url)
    if (data.video_url !== undefined) formData.append('video_url', data.video_url)
    if (data.surface_key !== undefined) formData.append('surface_key', data.surface_key)  // '' clears the binding
    if (data.is_published !== undefined) formData.append('is_published', String(data.is_published))
    if (data.is_pinned !== undefined) formData.append('is_pinned', String(data.is_pinned))
    if (data.sort_order !== undefined) formData.append('sort_order', String(data.sort_order))
    if (data.attachment) formData.append('attachment', data.attachment)
    if (data.video_file) formData.append('video_file', data.video_file)
    if (data.caption_file) formData.append('caption_file', data.caption_file)
    if (data.remove_video) formData.append('remove_video', 'true')
    if (data.remove_file) formData.append('remove_file', 'true')
    if (data.remove_caption) formData.append('remove_caption', 'true')

    const response = await apiClient.patch<TrainingMaterial>(`${this.baseUrl}/training/${materialId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  }

  async getTrainingVideoUrl(materialId: string): Promise<{
    video_url: string
    filename?: string
    size_bytes?: number
    expires_in_hours?: number
  }> {
    const response = await apiClient.get<{
      video_url: string
      filename?: string
      size_bytes?: number
      expires_in_hours?: number
    }>(`${this.baseUrl}/training/${materialId}/video-url`)
    return response.data
  }

  async getTrainingCaptionUrl(materialId: string): Promise<{
    caption_url: string
    filename?: string
    expires_in_hours?: number
  }> {
    const response = await apiClient.get<{
      caption_url: string
      filename?: string
      expires_in_hours?: number
    }>(`${this.baseUrl}/training/${materialId}/caption-url`)
    return response.data
  }

  async getTrainingFileUrl(materialId: string): Promise<{
    file_url: string
    filename?: string
    size_bytes?: number
    expires_in_hours?: number
  }> {
    const response = await apiClient.get<{
      file_url: string
      filename?: string
      size_bytes?: number
      expires_in_hours?: number
    }>(`${this.baseUrl}/training/${materialId}/file-url`)
    return response.data
  }

  async deleteTrainingMaterial(materialId: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/training/${materialId}`)
  }

  // ---------- Silent Background Check ----------

  /**
   * Check for unread responses using native fetch (bypasses apiClient interceptors).
   * This prevents 401 errors from triggering the token refresh cascade and
   * marking the session as expired when the backend tables don't exist yet.
   */
  async checkUnreadSilent(): Promise<{ hasUnread: boolean }> {
    try {
      const token = authService.getToken()
      if (!token) return { hasUnread: false }

      const baseURL = API_CONFIG.baseURL || ''
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }

      // Add test user headers if applicable
      const enableTestUsers = import.meta.env.DEV || import.meta.env.VITE_ENABLE_TEST_USERS === 'true'
      if (enableTestUsers) {
        const devPersonaStr = localStorage.getItem('dev_selected_persona')
        if (devPersonaStr) {
          try {
            const devPersona = JSON.parse(devPersonaStr)
            headers['X-Test-User-Id'] = devPersona.personnelNumber || 'TEST001'
            headers['X-Test-User-Roles'] = (devPersona.roles || ['employee']).join(',')
          } catch { /* ignore */ }
        }
      }

      const [ticketRes, suggestionRes] = await Promise.allSettled([
        fetch(`${baseURL}${this.baseUrl}/tickets/mine`, { headers }),
        fetch(`${baseURL}${this.baseUrl}/suggestions/mine`, { headers }),
      ])

      let hasUnread = false

      if (ticketRes.status === 'fulfilled' && ticketRes.value.ok) {
        const data = await ticketRes.value.json()
        if (data.tickets?.some((t: any) => t.has_new_response)) hasUnread = true
      }
      if (suggestionRes.status === 'fulfilled' && suggestionRes.value.ok) {
        const data = await suggestionRes.value.json()
        if (data.suggestions?.some((s: any) => s.has_new_response)) hasUnread = true
      }

      return { hasUnread }
    } catch {
      return { hasUnread: false }
    }
  }

  // ---------- Stats ----------

  async getStats(): Promise<SupportStats> {
    const response = await apiClient.get<SupportStats>(`${this.baseUrl}/stats`)
    return response.data
  }
}

export const supportService = new SupportService()
export default supportService
